import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api";
import SearchableSelect from "../../components/SearchableSelect";
import ProjectMemberPicker from "../../components/ProjectMemberPicker";

const initialState = {
  name: "",
  code: "",
  description: "",
  department_id: "",
  unit_id: "",
  owner_user_id: "",
};

// Quick "+ New Project" flow launched from the Create Work Ticket form when
// the project a user needs isn't in the list yet. Reuses the EXISTING
// project-create API (POST /projects, the same admin Project Management
// endpoint) -- no second project table, no duplicated validation. Visibility
// is gated by the caller via <Can moduleKey="projects" actionKey="create">,
// so this component assumes the caller is already permitted.
export default function QuickCreateProjectModal({ show, onClose, onCreated }) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [units, setUnits] = useState([]);
  const [owners, setOwners] = useState([]);

  useEffect(() => {
    if (!show) return;
    api.get("/departments", { params: { is_active: 1, pageSize: 200 } })
      .then((res) => setDepartments(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setDepartments([]));
    api.get("/units")
      .then((res) => setUnits(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []))
      .catch(() => setUnits([]));
    api.get("/tasks/eligible-users", { params: { purpose: "assigned_to" } })
      .then((res) => setOwners(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setOwners([]));
  }, [show]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const reset = () => setForm(initialState);

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.warning("Project name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/projects", {
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        description: form.description.trim() || null,
        department_id: form.department_id || null,
        unit_id: form.unit_id || null,
        owner_user_id: form.owner_user_id || null,
      });
      toast.success("Project created");
      reset();
      onCreated(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="qcp-modal">
      <Modal.Header closeButton>
        <Modal.Title>New Project</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-8">
            <Form.Label>Project Name *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Customer Portal Revamp"
              autoFocus
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Project Code</Form.Label>
            <Form.Control
              value={form.code}
              onChange={(e) => handleChange("code", e.target.value)}
              placeholder="Auto"
            />
            <Form.Text className="text-muted">Leave blank to auto-generate from the name.</Form.Text>
          </div>
          <div className="col-md-12">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="What is this project for?"
            />
          </div>
          <div className="col-md-6">
            <Form.Label>Owner (optional — defaults to you)</Form.Label>
            <ProjectMemberPicker
              users={owners}
              value={form.owner_user_id}
              onChange={(id) => handleChange("owner_user_id", id)}
              placeholder="You"
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Department (optional)</Form.Label>
            <SearchableSelect
              options={departments.map((d) => ({ value: String(d.id), label: d.name }))}
              value={form.department_id}
              onChange={(v) => handleChange("department_id", v)}
              placeholder="Unassigned"
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Unit (optional)</Form.Label>
            <SearchableSelect
              options={units.map((u) => ({ value: String(u.id), label: u.name }))}
              value={form.unit_id}
              onChange={(v) => handleChange("unit_id", v)}
              placeholder="Unassigned"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button className="unit-btn unit-btn-primary" onClick={handleCreate} disabled={saving}>
          {saving ? "Creating..." : "Create Project"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
